import { File, Folder, FolderOpen, FileImage } from "@phosphor-icons/react";
import { useMemo } from "react";
import TreeView, { flattenTree } from "react-accessible-treeview";
import { isString } from "lodash-es";
import { isImageFile } from "../utils/file-types";

export interface TreeNode {
  id: string; // unique identifier for the node, using full path
  name: string;
  children?: TreeNode[];
}

// Adapted type for model source (Record<string, string | Blob>)
export type ModelSource = Record<string, string | Blob>;

export function convertToTreeView(source: ModelSource): TreeNode {
  const root: TreeNode = {
    name: "",
    children: [],
    id: "", // root id is empty string
  };

  // Create a map to track all nodes by their full path
  const nodeMap = new Map<string, TreeNode>();
  nodeMap.set("", root);

  // Get all paths from the keys of the source object
  const sortedPaths = Object.keys(source);

  for (const path of sortedPaths) {
    const pathParts = path.split("/").filter((part) => part.length > 0);

    // Build the path incrementally, ensuring all parent directories exist
    let currentPath = "";
    let currentNode = root;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      const newPath = currentPath ? `${currentPath}/${part}` : part;

      if (!nodeMap.has(newPath)) {
        // Create new node
        const isFile = i === pathParts.length - 1; // Files are leaf nodes
        const newNode: TreeNode = {
          name: part,
          id: newPath, // Use the full path as the ID
          ...(isFile
            ? {
                metadata: {},
              }
            : { children: [] }),
        };

        // Add to current node's children
        if (!currentNode.children) {
          currentNode.children = [];
        }
        currentNode.children.push(newNode);

        // Track in map
        nodeMap.set(newPath, newNode);
        currentNode = newNode;
      } else {
        // Node already exists, move to it
        const nextNode = nodeMap.get(newPath);
        if (!nextNode) {
          throw new Error(`Node for path "${newPath}" not found in nodeMap.`);
        }
        currentNode = nextNode;
      }

      currentPath = newPath;
    }
  }

  return root;
}

export type FileTreeDecorationType = "modified" | "added" | "removed";

type FileTreeDecorations = Record<
  string,
  {
    type: FileTreeDecorationType;
  }
>;

export function FileTree({
  source,
  onSelectNode,
  selectedId,
  decorations = {},
}: {
  source: ModelSource;
  onSelectNode: (id: string) => void;
  selectedId: string;
  decorations?: FileTreeDecorations;
}) {
  const tree = useMemo(() => {
    return flattenTree(convertToTreeView(source));
  }, [source]);

  return (
    <TreeView
      selectedIds={[selectedId]}
      data={tree}
      className="basic"
      multiSelect={false}
      propagateSelectUpwards={false}
      nodeAction="check"
      clickAction="EXCLUSIVE_SELECT"
      aria-label="Model files"
      onNodeSelect={({ element }) => {
        if (element.children.length > 0) {
          return;
        }

        if (!isString(element.id)) {
          console.warn("Node ID is not a string:", element.id);
          return;
        }
        onSelectNode(element.id);
      }}
      nodeRenderer={({
        element,
        getNodeProps,
        level,
        isSelected,
        isExpanded,
      }) => {
        const modified =
          decorations[element.id] &&
          decorations[element.id].type === "modified";

        const added =
          decorations[element.id] && decorations[element.id].type === "added";

        return (
          <div
            {...getNodeProps()}
            style={{ paddingLeft: 20 * (level - 1) }}
            data-selected={isSelected && element.children.length === 0}
            title={element.name}
            data-modified={modified}
            data-added={added}
            className="hover:bg-r8-gray-a3 block w-full cursor-pointer data-[selected=true]:bg-r8-gray-5 data-[selected=true]:font-semibold data-[modified=true]:text-r8-green-10 data-[added=true]:text-r8-blue-10"
          >
            <div className="px-2 py-1.5 flex items-center gap-2 text-r8-sm justify-between overflow-hidden">
              <div className="flex items-center gap-2 truncate">
                <div className="shrink-0">
                  {element.children.length > 0 ? (
                    isExpanded ? (
                      <FolderOpen />
                    ) : (
                      <Folder />
                    )
                  ) : isImageFile(element.name) ? (
                    <FileImage className="text-r8-blue-9" />
                  ) : (
                    <File />
                  )}
                </div>
                <span className="truncate select-none">{element.name}</span>
              </div>
              {modified ? (
                <span className="shrink-0 text-r8-sm text-r8-green-10">M</span>
              ) : null}
              {added ? (
                <span className="shrink-0 text-r8-sm text-r8-blue-10">A</span>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
}
